import React, { useState, useEffect } from 'react';
import { notification } from '../../hocs/notification';
import axios from 'axios';
import { mainurl } from '../../../Mainurl';

import {
    FaHeading,
    FaUser,
    FaEnvelope,
    FaPhone,
    FaUserTag,
    FaLock,
    FaCheckCircle,
    FaTimesCircle,
    FaSave,
    FaArrowLeft,
    FaEye,
    FaEyeSlash,
    FaLanguage
} from 'react-icons/fa';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Form,
    FormGroup,
    Input,
    Label,
    InputGroup,
    InputGroupText,
    Spinner,
    Button,
    FormFeedback,
    Badge,
    Alert,
    Progress
} from 'reactstrap';

const AddUser = ({ back, reset }) => {
    const [hindiNameLoading, setHindiNameLoading] = useState(false);
    const [name, setName] = useState('');
    const [nameHin, setNameHin] = useState('');
    const [mobileNo, setMobileNo] = useState('');
    const [role, setRole] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [cfmPassword, setCfmPassword] = useState('');
    const [passwordValid, setPasswordValid] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [roleOpt, setRoleOpt] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState(0);

    // Form validation states
    const [touched, setTouched] = useState({});

    useEffect(() => {
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            let postData = [{ "org_id": sessionStorage.getItem('org_id') }];
            const response = await axios({
                method: 'post',
                url: mainurl + 'Master/box_role_list',
                data: postData,
                headers: { 'Content-Type': 'application/JSON' }
            });

            setRoleOpt(response.data);
        } catch (error) {
            let notify = notification({
                message: "Failed to load roles. Please try again.",
                type: 'error'
            });
            notify();
        }
    };

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (/[a-z]/.test(password)) strength += 25;
        if (/[A-Z]/.test(password)) strength += 25;
        if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) strength += 25;
        return strength;
    };

    const handlePasswordChange = (value) => {
        setPassword(value);
        setPasswordStrength(calculatePasswordStrength(value));
        
        // Recheck password match if confirm password is already entered
        if (cfmPassword) {
            setPasswordValid(value === cfmPassword);
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!name.trim()) errors.name = 'Name is required';
        if (!nameHin.trim()) errors.nameHin = 'Hindi name is required';
        if (!mobileNo.trim()) {
            errors.mobileNo = 'Mobile number is required';
        } else if (!/^\d{10}$/.test(mobileNo)) {
            errors.mobileNo = 'Mobile number must be 10 digits';
        }
        if (!role) errors.role = 'Please select a role';
        if (!password) {
            errors.password = 'Password is required';
        } else if (password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }
        if (!cfmPassword) {
            errors.cfmPassword = 'Please confirm your password';
        } else if (password !== cfmPassword) {
            errors.cfmPassword = 'Passwords do not match';
        }
        if (email && !/\S+@\S+\.\S+/.test(email)) {
            errors.email = 'Please enter a valid email address';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const add_handler = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            let notify = notification({
                message: "Please correct the errors below",
                type: 'warn'
            });
            notify();
            return;
        }

        setIsSubmitting(true);

        try {
            let postData = [{
                "org_id": sessionStorage.getItem('org_id'),
                "user_id": '10001',
                "user_name": name.trim(),
                "user_name_ne": nameHin.trim(),
                "user_email": email.trim() || null,
                "user_phone": mobileNo.trim(),
                "role_code": role,
                "password": password,
            }];

            const response = await axios({
                method: 'post',
                url: mainurl + 'Master/box_user_add',
                data: postData,
                headers: { 'Content-Type': 'application/JSON' }
            });

            let user_id = response.data[0].retcode;
            let notify = notification({
                message: `User added successfully! User ID: ${user_id}`,
                type: 'success',
                time: false,
                position: 'center'
            });
            notify();
            reset();
        } catch (error) {
            let notify = notification({
                message: "Failed to add user. Please try again.",
                type: 'error'
            });
            notify();
        } finally {
            setIsSubmitting(false);
        }
    };

    const translate_text_handler = async () => {
        if (!name.trim()) {
            let notify = notification({
                message: "Please enter name first",
                type: 'warn'
            });
            notify();
            return;
        }

        setHindiNameLoading(true);
        // Add your translation API call here
        // For now, just a placeholder
        setTimeout(() => {
            setNameHin(name); // Placeholder - replace with actual translation
            setHindiNameLoading(false);
        }, 1000);
    };

    const check_valid = (pass) => {
        setCfmPassword(pass);
        setPasswordValid(password === pass);
    };

    const handleBlur = (fieldName) => {
        setTouched(prev => ({ ...prev, [fieldName]: true }));
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength <= 25) return 'danger';
        if (passwordStrength <= 50) return 'warning';
        if (passwordStrength <= 75) return 'info';
        return 'success';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength <= 25) return 'Weak';
        if (passwordStrength <= 50) return 'Fair';
        if (passwordStrength <= 75) return 'Good';
        return 'Strong';
    };

    return (
        <>
            <Card className='border-0 shadow-sm'>
                <CardHeader className="bg-gradient-primary text-white">
                    <div className="d-flex align-items-center gap-2">
                        <FaUser size={18} />
                        <h5 className="mb-0">Add New User</h5>
                    </div>
                </CardHeader>
                <CardBody className="p-4">
                    <Form onSubmit={add_handler}>
                        <Row>
                            {/* Basic Information Section */}
                            <Col xs={12}>
                                <div className="mb-4">
                                    <h6 className="text-primary mb-3 d-flex align-items-center gap-2">
                                        <FaUser size={14} />
                                        Basic Information
                                    </h6>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="name" className="form-label fw-medium">
                                                    Name <span className="text-danger">*</span>
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light">
                                                        <FaUser className="text-muted" size={14} />
                                                    </InputGroupText>
                                                    <Input
                                                        id="name"
                                                        type='text'
                                                        placeholder="Enter full name"
                                                        value={name}
                                                        onChange={(e) => setName(e.target.value)}
                                                        onBlur={() => handleBlur('name')}
                                                        onKeyPress={(e) => !/^[a-zA-Z\s]*$/.test(e.key) && e.preventDefault()}
                                                        invalid={touched.name && formErrors.name}
                                                        required
                                                    />
                                                </InputGroup>
                                                {touched.name && formErrors.name && (
                                                    <FormFeedback>{formErrors.name}</FormFeedback>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="hName" className="form-label fw-medium">
                                                    Name (Hindi) <span className="text-danger">*</span>
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light">
                                                        <FaLanguage className="text-muted" size={14} />
                                                    </InputGroupText>
                                                    <Input
                                                        id="hName"
                                                        type='text'
                                                        placeholder="Enter name in Hindi"
                                                        value={nameHin}
                                                        onChange={(e) => setNameHin(e.target.value)}
                                                        onBlur={() => handleBlur('nameHin')}
                                                        invalid={touched.nameHin && formErrors.nameHin}
                                                        required
                                                    />
                                                    <Button
                                                        color="primary"
                                                        outline
                                                        disabled={hindiNameLoading || !name.trim()}
                                                        onClick={translate_text_handler}
                                                        title="Auto-translate from English name"
                                                    >
                                                        {hindiNameLoading ? (
                                                            <Spinner size="sm" />
                                                        ) : (
                                                            <FaHeading size={14} />
                                                        )}
                                                    </Button>
                                                </InputGroup>
                                                {touched.nameHin && formErrors.nameHin && (
                                                    <FormFeedback>{formErrors.nameHin}</FormFeedback>
                                                )}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>

                            {/* Contact Information Section */}
                            <Col xs={12}>
                                <div className="mb-4">
                                    <h6 className="text-primary mb-3 d-flex align-items-center gap-2">
                                        <FaEnvelope size={14} />
                                        Contact Information
                                    </h6>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="email" className="form-label fw-medium">
                                                    Email Address
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light">
                                                        <FaEnvelope className="text-muted" size={14} />
                                                    </InputGroupText>
                                                    <Input
                                                        id="email"
                                                        type='email'
                                                        placeholder="Enter email address"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        onBlur={() => handleBlur('email')}
                                                        invalid={touched.email && formErrors.email}
                                                    />
                                                </InputGroup>
                                                {touched.email && formErrors.email && (
                                                    <FormFeedback>{formErrors.email}</FormFeedback>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="mobileNo" className="form-label fw-medium">
                                                    Mobile Number <span className="text-danger">*</span>
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light">
                                                        <FaPhone className="text-muted" size={14} />
                                                    </InputGroupText>
                                                    <Input
                                                        id="mobileNo"
                                                        type='tel'
                                                        placeholder="Enter 10-digit mobile number"
                                                        maxLength={10}
                                                        value={mobileNo}
                                                        onChange={(e) => setMobileNo(e.target.value)}
                                                        onBlur={() => handleBlur('mobileNo')}
                                                        onKeyPress={(e) => !/[0-9]/.test(e.key) && e.preventDefault()}
                                                        invalid={touched.mobileNo && formErrors.mobileNo}
                                                        required
                                                    />
                                                </InputGroup>
                                                {touched.mobileNo && formErrors.mobileNo && (
                                                    <FormFeedback>{formErrors.mobileNo}</FormFeedback>
                                                )}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>

                            {/* Role & Access Section */}
                            <Col xs={12}>
                                <div className="mb-4">
                                    <h6 className="text-primary mb-3 d-flex align-items-center gap-2">
                                        <FaUserTag size={14} />
                                        Role & Access
                                    </h6>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="role" className="form-label fw-medium">
                                                    User Role <span className="text-danger">*</span>
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light">
                                                        <FaUserTag className="text-muted" size={14} />
                                                    </InputGroupText>
                                                    <Input
                                                        id="role"
                                                        type="select"
                                                        value={role}
                                                        onChange={(e) => setRole(e.target.value)}
                                                        onBlur={() => handleBlur('role')}
                                                        invalid={touched.role && formErrors.role}
                                                        required
                                                    >
                                                        <option value="">-- Select User Role --</option>
                                                        {roleOpt.map((roleItem, index) => (
                                                            <option key={index} value={roleItem.role_code}>
                                                                {roleItem.role_desc}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                </InputGroup>
                                                {touched.role && formErrors.role && (
                                                    <FormFeedback>{formErrors.role}</FormFeedback>
                                                )}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>

                            {/* Security Section */}
                            <Col xs={12}>
                                <div className="mb-4">
                                    <h6 className="text-primary mb-3 d-flex align-items-center gap-2">
                                        <FaLock size={14} />
                                        Security
                                    </h6>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="password" className="form-label fw-medium">
                                                    Password <span className="text-danger">*</span>
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light">
                                                        <FaLock className="text-muted" size={14} />
                                                    </InputGroupText>
                                                    <Input
                                                        id="password"
                                                        type={showPassword ? 'text' : 'password'}
                                                        placeholder="Enter password"
                                                        value={password}
                                                        onChange={(e) => handlePasswordChange(e.target.value)}
                                                        onBlur={() => handleBlur('password')}
                                                        invalid={touched.password && formErrors.password}
                                                        required
                                                    />
                                                    <Button
                                                        color="link"
                                                        className="border-0"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        type="button"
                                                    >
                                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                    </Button>
                                                </InputGroup>
                                                {password && (
                                                    <div className="mt-2">
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <small className="text-muted">Password Strength:</small>
                                                            <Badge color={getPasswordStrengthColor()} pill>
                                                                {getPasswordStrengthText()}
                                                            </Badge>
                                                        </div>
                                                        <Progress
                                                            value={passwordStrength}
                                                            color={getPasswordStrengthColor()}
                                                            style={{ height: '4px' }}
                                                        />
                                                    </div>
                                                )}
                                                {touched.password && formErrors.password && (
                                                    <FormFeedback>{formErrors.password}</FormFeedback>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="cpassword" className="form-label fw-medium">
                                                    Confirm Password <span className="text-danger">*</span>
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light">
                                                        <FaLock className="text-muted" size={14} />
                                                    </InputGroupText>
                                                    <Input
                                                        id="cpassword"
                                                        type={showConfirmPassword ? 'text' : 'password'}
                                                        placeholder="Confirm password"
                                                        value={cfmPassword}
                                                        onChange={(e) => check_valid(e.target.value)}
                                                        onBlur={() => handleBlur('cfmPassword')}
                                                        valid={cfmPassword && passwordValid}
                                                        invalid={cfmPassword && !passwordValid}
                                                        required
                                                    />
                                                    <Button
                                                        color="link"
                                                        className="border-0"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        type="button"
                                                    >
                                                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                                    </Button>
                                                </InputGroup>
                                                {cfmPassword && (
                                                    <div className="mt-1">
                                                        {passwordValid ? (
                                                            <small className="text-success d-flex align-items-center gap-1">
                                                                <FaCheckCircle size={12} />
                                                                Passwords match
                                                            </small>
                                                        ) : (
                                                            <small className="text-danger d-flex align-items-center gap-1">
                                                                <FaTimesCircle size={12} />
                                                                Passwords do not match
                                                            </small>
                                                        )}
                                                    </div>
                                                )}
                                                {touched.cfmPassword && formErrors.cfmPassword && (
                                                    <FormFeedback>{formErrors.cfmPassword}</FormFeedback>
                                                )}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </CardBody>
                <CardFooter className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                        <Button
                            color="secondary"
                            outline
                            onClick={back}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaArrowLeft size={14} />
                            Back to List
                        </Button>
                        <Button
                            color="primary"
                            onClick={add_handler}
                            disabled={isSubmitting}
                            className="d-flex align-items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner size="sm" />
                                    Creating User...
                                </>
                            ) : (
                                <>
                                    <FaSave size={14} />
                                    Create User
                                </>
                            )}
                        </Button>
                    </div>
                </CardFooter>
            </Card>

            {/* Help Section */}
            <Alert color="info" className="mt-3 border-0 shadow-sm">
                <div className="d-flex align-items-start gap-2">
                    <FaUser className="text-info mt-1" size={16} />
                    <div>
                        <h6 className="alert-heading mb-2">User Creation Guidelines</h6>
                        <ul className="mb-0 small">
                            <li>Fields marked with <span className="text-danger">*</span> are required</li>
                            <li>Mobile number must be exactly 10 digits</li>
                            <li>Password should be at least 6 characters long</li>
                            <li>Use the translate button to auto-generate Hindi name</li>
                            <li>Email address is optional but recommended</li>
                        </ul>
                    </div>
                </div>
            </Alert>
        </>
    );
};

export default AddUser;